export class Rectangle {
    width: number;
    height: number;
    x: number;
    y: number;
    constructor(width, height, x, y) {
        this.width = width;
        this.height = height;
        this.x = x;
        this.y = y;
    }
    maxX = (): number => this.x + this.width;
    maxY = (): number => this.y + this.height;
    area = () => this.width * this.height;
    areaPPCM = (ppcm: number) => (this.width / ppcm) * (this.height / ppcm);
    equals = (rectangleToCompare: Rectangle) => this.width.toFixed(4) == rectangleToCompare.width.toFixed(4) &&
        this.height.toFixed(4) == rectangleToCompare.height.toFixed(4) &&
        this.x == rectangleToCompare.x &&
        this.y == rectangleToCompare.y;
}

const distinct = (n, i, array) => array.indexOf(n) === i;

export const rectangleArea = (r: Rectangle) => r.width * r.height;

const rectangleSplitAtXPosition = (rectangle: Rectangle, x: number): Rectangle[] => {
    if (x <= rectangle.x || x >= rectangle.x + rectangle.width) {
        return [rectangle]
    }
    let rect1 = new Rectangle(
        x - rectangle.x,
        rectangle.height,
        rectangle.x,
        rectangle.y
    )
    let rect2 = new Rectangle(
        rectangle.maxX() - x,
        rectangle.height,
        x,
        rectangle.y
    )
    return [rect1, rect2]
}

const rectangleSplitAtYPosition = (rectangle: Rectangle, y: number): Rectangle[] => {
    if (y <= rectangle.y || y >= rectangle.y + rectangle.height) {
        return [rectangle]
    }
    let rect1 = new Rectangle(
        rectangle.width,
        y - rectangle.y,
        rectangle.x,
        rectangle.y
    )
    let rect2 = new Rectangle(
        rectangle.width,
        rectangle.maxY() - y,
        rectangle.x,
        y
    )
    return [rect1, rect2]
}

const rectangleIncludeXPosition = (rectangle: Rectangle, x: number): boolean => rectangle.x < x && rectangle.maxX() > x;

const rectangleIncludeYPosition = (rectangle: Rectangle, y: number): boolean => rectangle.y < y && rectangle.maxY() > y;

const rectangleNotIncludeXPosition = (rectangle: Rectangle, x: number): boolean => rectangle.x >= x || rectangle.maxX() <= x;

const rectangleNotIncludeYPosition = (rectangle: Rectangle, y: number): boolean => rectangle.y >= y || rectangle.maxY() <= y;

export const rectanglesDistinctArea = (rectangles: Rectangle[], ppcm?: number): number => {
    // all x values
    const xPositions: number[] = rectangles.reduce((acc: number[], rectangle: Rectangle) => [...acc, rectangle.x, rectangle.maxX()], [])
        .filter(distinct)
        .sort((v1, v2) => v1 - v2);

    let rectanglesOutputByXPosition: Rectangle[] = [...rectangles];

    xPositions.forEach(xPosition => {
        const rectanglesToDivide = rectanglesOutputByXPosition.filter(rectangle => rectangleIncludeXPosition(rectangle, xPosition));
        const rectanglesToNotDivide = rectanglesOutputByXPosition.filter(rectangle => rectangleNotIncludeXPosition(rectangle, xPosition));
        const rectanglesDivided = rectanglesToDivide.map(rectangle => rectangleSplitAtXPosition(rectangle, xPosition))
            .reduce((acc, val) => acc.concat(val), []); // flatMap
        rectanglesOutputByXPosition = [...rectanglesToNotDivide, ...rectanglesDivided];
    });

    const yPositions: number[] = rectanglesOutputByXPosition.reduce((acc: number[], rectangle: Rectangle) => [...acc, rectangle.y, rectangle.maxY()], [])
        .filter(distinct)
        .sort((v1, v2) => v1 - v2);

    let rectanglesOutputByYPosition: Rectangle[] = [...rectanglesOutputByXPosition];

    yPositions.forEach(yPosition => {
        const rectanglesToDivide = rectanglesOutputByYPosition.filter(rectangle => rectangleIncludeYPosition(rectangle, yPosition));
        const rectanglesToNotDivide = rectanglesOutputByYPosition.filter(rectangle => rectangleNotIncludeYPosition(rectangle, yPosition));
        const rectanglesDivided = rectanglesToDivide.map(rectangle => rectangleSplitAtYPosition(rectangle, yPosition))
            .reduce((acc, val) => acc.concat(val), []); // flatMap
        rectanglesOutputByYPosition = [...rectanglesToNotDivide, ...rectanglesDivided];
    });

    return rectanglesOutputByYPosition
        .filter((rect: Rectangle, index: number, arr: Rectangle[]) => arr.findIndex(r => r.equals(rect)) == index) // distinct rectangle
        .reduce((acc: number, rect: Rectangle) => ppcm ? acc + rect.areaPPCM(ppcm) : acc + rect.area(), 0); // sum all rectangles area
}